// ===========================================================================
// ProCare.SG — Services & Special Solutions catalogue (mock/static content)
// Each entry drives its dynamic detail page at /services/:slug or
// /special-solutions/:slug. A backend can later replace this via getServices().
// ===========================================================================

export const SERVICES = [
    {
        slug: "moving",
        name: "Moving",
        short: "Careful, coordinated moving for homes, offices and commercial spaces.",
        icon: "Truck",
        image: "/images/moving.webp",
        seoTitle: "Moving Services Singapore | House & Office Movers",
        intro:
            "Careful and coordinated moving services for homes, offices and commercial spaces. We plan, protect and transport your belongings so your move stays smooth from start to finish.",
        forWhom: ["Homeowners", "Tenants", "Landlords", "Businesses", "Property Agents"],
        scope: [
            "Residential moving",
            "Office moving",
            "Packing and unpacking",
            "Furniture protection",
            "Short-distance relocation",
            "Move-in and move-out support",
        ],
        useCases: [
            "Moving into a new HDB flat or condominium",
            "Office relocation with minimal downtime",
            "End-of-tenancy move-outs for tenants",
            "Coordinating a move alongside cleaning and painting",
        ],
        benefits: [
            "One team that coordinates your whole move",
            "Furniture wrapped and protected in transit",
            "Labelled boxes for a stress-free unpack",
            "Flexible scheduling around your handover",
        ],
    },
    {
        slug: "cleaning",
        name: "Cleaning",
        short: "Professional cleaning to prepare your property for move-in, handover or occupancy.",
        icon: "Sparkles",
        image: "/images/cleaning.webp",
        seoTitle: "Move-in & Move-out Cleaning Services Singapore",
        intro:
            "Professional cleaning services to prepare your property for move-in, handover or occupancy. From deep cleaning to post-renovation clean-ups, we leave every corner spotless.",
        forWhom: ["Homeowners", "Tenants", "Landlords", "Property Agents", "Businesses"],
        scope: [
            "Move-in cleaning",
            "Move-out cleaning",
            "Post-renovation cleaning",
            "Deep cleaning",
            "Office cleaning",
            "Handover cleaning",
        ],
        useCases: [
            "Getting your deposit back with a spotless move-out",
            "Cleaning an empty HDB flat before handover",
            "Clearing dust after a renovation",
            "Preparing a listing for viewings",
        ],
        benefits: [
            "Detailed, checklist-driven cleaning",
            "Handover-ready results",
            "Eco-conscious products on request",
            "Bundled with painting and repairs",
        ],
    },
    {
        slug: "painting",
        name: "Painting",
        short: "Reliable residential & commercial painting with proper prep, protection and finishing.",
        icon: "PaintRoller",
        image: "/images/painting.webp",
        seoTitle: "HDB & Condo Painting Services Singapore",
        intro:
            "Reliable residential and commercial painting with proper preparation, protection and finishing. Our focus is on workmanship, surface preparation, quality paint products and attention to detail.",
        forWhom: ["Homeowners", "Landlords", "Property Agents", "Businesses"],
        scope: [
            "HDB painting",
            "Condominium painting",
            "Landed-property painting",
            "Office and retail painting",
            "Wall and ceiling touch-ups",
            "Repainting before handover",
        ],
        useCases: [
            "Refreshing a home before moving in",
            "Repainting before a property handover",
            "Touching up walls for a listing",
            "Recolouring an office or retail space",
        ],
        benefits: [
            "Thorough surface preparation",
            "Floors and furniture properly protected",
            "Clean lines and even finishing",
            "Quality, low-odour paint products",
        ],
    },
    {
        slug: "aircon-servicing",
        name: "Aircon Servicing",
        short: "Servicing, troubleshooting and cleaning for residential and commercial aircon systems.",
        icon: "Wind",
        image: "/images/aircon.webp",
        seoTitle: "Aircon Servicing Singapore | Residential & Commercial",
        intro:
            "Routine servicing, troubleshooting and cleaning for residential and commercial air-conditioning systems, keeping your units running cool and clean.",
        forWhom: ["Homeowners", "Tenants", "Landlords", "Businesses"],
        scope: [
            "Routine servicing",
            "Chemical cleaning",
            "Troubleshooting",
            "Filter and coil cleaning",
            "Gas top-up checks",
            "Multi-unit servicing",
        ],
        useCases: [
            "Preparing units before a move-in",
            "Regular maintenance to prevent breakdowns",
            "Handover servicing for tenants",
            "Office system upkeep",
        ],
        benefits: [
            "Cleaner, healthier air",
            "Improved cooling efficiency",
            "Fewer unexpected breakdowns",
            "Bundled with move-in packages",
        ],
    },
    {
        slug: "floor-polishing",
        name: "Floor Polishing",
        short: "Restore the shine of marble, tiles and suitable flooring surfaces.",
        icon: "Gem",
        image: "/images/floor-polishing.webp",
        seoTitle: "Floor Polishing Singapore | Marble & Tile Restoration",
        intro:
            "Restore the shine and appearance of marble, tiles and suitable flooring surfaces with professional polishing and restoration.",
        forWhom: ["Homeowners", "Landlords", "Property Agents", "Businesses"],
        scope: [
            "Marble polishing",
            "Tile restoration",
            "Vinyl and homogeneous flooring care",
            "Scratch and stain reduction",
            "Grinding and buffing",
            "Sealing and protection",
        ],
        useCases: [
            "Reviving dull floors before a listing",
            "Restoring shine after renovation",
            "Preparing a unit for handover",
            "Commercial lobby maintenance",
        ],
        benefits: [
            "Renewed, glossy floors",
            "Reduced scratches and dullness",
            "Longer-lasting surfaces",
            "Great for listing photos",
        ],
    },
    {
        slug: "handyman-works",
        name: "Handyman Works",
        short: "Convenient help for installations, adjustments and everyday property maintenance.",
        icon: "Wrench",
        image: "/images/handyman.webp",
        seoTitle: "Handyman Services Singapore | Home Maintenance",
        intro:
            "Convenient assistance for installations, adjustments and everyday property maintenance, handled by an experienced team.",
        forWhom: ["Homeowners", "Tenants", "Landlords", "Businesses"],
        scope: [
            "Fixture installations",
            "Shelving and mounting",
            "Door and hinge adjustments",
            "Minor plumbing fittings",
            "Curtain and blind fitting",
            "General property fixes",
        ],
        useCases: [
            "Setting up a new home after moving in",
            "Fixing small defects before handover",
            "Mounting TVs, shelves and fixtures",
            "Ongoing property upkeep",
        ],
        benefits: [
            "One call for many small jobs",
            "Tidy, careful workmanship",
            "Flexible scheduling",
            "Bundled with move-in packages",
        ],
    },
    {
        slug: "repair-works",
        name: "Repair Works",
        short: "Repairs for walls, ceilings, fixtures, doors, cabinets and property defects.",
        icon: "Hammer",
        image: "/images/repair.webp",
        seoTitle: "Home Repair Services Singapore | Defect Repairs",
        intro:
            "Repairs for walls, ceilings, fixtures, doors, cabinets and other property defects, restoring your space to good condition.",
        forWhom: ["Homeowners", "Tenants", "Landlords", "Property Agents"],
        scope: [
            "Wall and ceiling patching",
            "Door and cabinet repairs",
            "Fixture repairs",
            "Crack filling",
            "Defect rectification",
            "Handover touch-ups",
        ],
        useCases: [
            "Fixing defects listed at handover",
            "Restoring a unit before a sale",
            "Repairing wear and tear",
            "Preparing for new tenants",
        ],
        benefits: [
            "Restore your property's condition",
            "Handover-ready results",
            "Coordinated with painting",
            "Careful, tidy repairs",
        ],
    },
    {
        slug: "dismantling-disposal",
        name: "Dismantling & Disposal",
        short: "Safe dismantling of fittings plus responsible removal of unwanted items.",
        icon: "Recycle",
        image: "/images/dismantling.webp",
        seoTitle: "Furniture Dismantling & Disposal Services Singapore",
        intro:
            "Safe dismantling of cabinets, furniture, fittings and partitions, plus responsible removal and disposal of unwanted furniture, debris and household items.",
        forWhom: ["Homeowners", "Tenants", "Landlords", "Businesses", "Property Managers"],
        scope: [
            "Cabinet and furniture dismantling",
            "Partition removal",
            "Fitting and fixture removal",
            "Furniture disposal",
            "Debris clearance",
            "Household item removal",
        ],
        useCases: [
            "Clearing a unit before renovation",
            "Removing old furniture during a move-out",
            "Emptying a property for handover",
            "Office decommissioning",
        ],
        benefits: [
            "Safe, careful dismantling",
            "Responsible disposal",
            "Cleared, ready-to-work space",
            "Bundled with moving and cleaning",
        ],
    },
];

export const SPECIAL_SOLUTIONS = [
    {
        slug: "spalling-concrete-repairs",
        name: "Spalling Concrete Repairs",
        short: "Assessment, removal and repair of loose or damaged concrete surfaces.",
        icon: "ShieldAlert",
        image: "/images/spalling.webp",
        seoTitle: "Spalling Concrete Repair Singapore",
        intro:
            "Professional assessment, removal and repair of loose or damaged concrete surfaces. We restore affected ceilings and walls before completing the area with suitable patching and finishing works.",
        forWhom: ["Homeowners", "Landlords", "Property Managers", "Businesses"],
        scope: [
            "Assessment of affected area",
            "Removal of loose concrete",
            "Treatment of exposed reinforcement where necessary",
            "Patching and surface restoration",
            "Painting and finishing",
        ],
        useCases: [
            "Flaking or bulging ceiling concrete",
            "Rust stains around reinforcement bars",
            "Ceiling patches before handover",
            "Restoring affected walls and soffits",
        ],
        process: [
            "Assessment of the affected area",
            "Removal of loose concrete",
            "Treatment of exposed reinforcement where necessary",
            "Patching and surface restoration",
            "Painting and finishing",
        ],
        benefits: [
            "Restored ceiling and wall surfaces",
            "Neat patching and finishing",
            "Coordinated with painting",
            "Clear, transparent assessment",
        ],
        disclaimer:
            "We provide repair and restoration works. We do not offer structural-engineering guarantees.",
        cta: "Request an Inspection",
    },
    {
        slug: "mould-treatment",
        name: "Mould Treatment",
        short: "Inspection and treatment solutions for mould on walls, ceilings and indoor surfaces.",
        icon: "Droplets",
        image: "/images/mould.webp",
        seoTitle: "Mould Treatment Singapore | Mold Removal",
        intro:
            "Professional mould inspection and treatment solutions for affected walls, ceilings and indoor surfaces. Our team identifies visible problem areas, carries out appropriate cleaning and treatment, and advises on measures to reduce recurrence.",
        forWhom: ["Homeowners", "Tenants", "Landlords", "Property Managers", "Businesses"],
        scope: [
            "Site inspection",
            "Identification of affected areas",
            "Surface preparation",
            "Mould treatment",
            "Cleaning and finishing",
            "Anti-mould coating where applicable",
        ],
        useCases: [
            "Dark patches on walls or ceilings",
            "Musty smells in enclosed rooms",
            "Bathroom and kitchen surfaces",
            "Pre-handover surface treatment",
        ],
        process: [
            "Site inspection",
            "Identification of affected areas",
            "Surface preparation",
            "Mould treatment",
            "Cleaning and finishing",
            "Anti-mould coating where applicable",
            "Prevention recommendations",
        ],
        benefits: [
            "Cleaner, treated surfaces",
            "Advice to reduce recurrence",
            "Anti-mould coating options",
            "Coordinated with painting",
        ],
        disclaimer:
            "We provide inspection and treatment works. We do not make medical claims or guarantee complete removal.",
        cta: "Arrange a Mould Inspection",
    },
    {
        slug: "events-set-up",
        name: "Events Set Up",
        short: "End-to-end roadshow set-up, transportation and manpower for events across Singapore.",
        icon: "Tent",
        image: "/images/events.webp",
        seoTitle: "Roadshow & Events Set Up Singapore | Transportation & Manpower",
        intro:
            "From roadshows and product launches to exhibitions and pop-up stores, ProCare.SG provides complete events support — set-up and tear-down, transportation of equipment, and reliable on-site manpower. One coordinated team so your event runs smoothly from load-in to load-out.",
        forWhom: ["Businesses", "Event Organisers", "Retailers", "Property Agents", "Property Managers"],
        offerings: [
            {
                title: "Road Show Events Set Up & Tear Down",
                desc: "Full assembly and dismantling of booths, display stands, staging, backdrops and signage for mall atriums, showrooms and public spaces — set up on time and cleared away neatly after the event.",
            },
            {
                title: "Events Transportation",
                desc: "Safe delivery and collection of event equipment, displays, furniture and materials. We handle loading, transport, on-site unloading and return logistics so nothing gets left behind.",
            },
            {
                title: "Events Manpower Supply",
                desc: "Trained crew to support your event — from set-up and packing to on-the-day handling and tear-down. Scale your team up or down to match the size of your event.",
            },
        ],
        scope: [
            "Road show set-up and tear-down",
            "Booth, staging and display assembly",
            "Signage and backdrop installation",
            "Equipment and materials transportation",
            "Loading, delivery and on-site handling",
            "Trained events manpower supply",
        ],
        useCases: [
            "Mall and atrium roadshows",
            "Product launches and pop-up stores",
            "Property launch showcases",
            "Exhibitions and community events",
        ],
        process: [
            "Planning and site coordination",
            "Transportation of equipment and materials",
            "On-site set-up and assembly",
            "Manpower deployment during the event",
            "Tear-down and clearance",
            "Return transportation and disposal",
        ],
        benefits: [
            "One team for set-up, logistics and manpower",
            "Reliable, on-time delivery and load-in",
            "Trained, presentable on-site crew",
            "Smooth tear-down and clearance",
        ],
        cta: "Enquire About Events",
    },
];

// Featured (top 3) services for the homepage
export const FEATURED_SLUGS = ["moving", "cleaning", "painting"];

export const findService = (slug) =>
    SERVICES.find((s) => s.slug === slug) ||
    SPECIAL_SOLUTIONS.find((s) => s.slug === slug);
